import { Provider, inject } from '@angular/core';
import { Store } from '@ngxs/store';
import { ActionsOnReceive, NotificationPayload, VideoInterviewRequestCompletedData, VideoInterviewRequestFailedData } from '@tribuu-shared-notifications';
import type { TribuNotification } from '@tribuu-api';
import { RecruiterJobListItemDto, RecruiterPostulationItemDto } from '@tribuu-api';
import { CvScreeningLiveRefreshService } from '@modules/vacantes/services/cv-screening/cv-screening-live-refresh.service';
import { PostulationsStateService } from '@modules/vacantes/services/postulations-state.service';
import { VacantesStateService } from '@modules/vacantes/services/vacantes-state.service';
import { VideoInterviewProgressStore } from '@modules/vacantes/services/video-interview-progress.store';
import { NOTIFICATION_HANDLER, NotificationHandler } from './handler-registry';
import { NotificationActions } from './notifications.actions';

/**
 * Registers the NOTIFICATION_CREATED push-event handler.
 *
 * When the backend emits a NOTIFICATION_CREATED envelope (full TribuNotification
 * entity in payload.data.notification), this handler dispatches
 * NotificationActions.Add(entity) — updating the bell dropdown with zero HTTP calls.
 *
 * Usage: add to the `providers` array of AppModule next to provideIdentityVerificationHandler().
 */
export function provideNotificationCreatedHandler(): Provider {
  return {
    provide: NOTIFICATION_HANDLER,
    useFactory: (): NotificationHandler => {
      const store = inject(Store);
      return {
        action: ActionsOnReceive.NOTIFICATION_CREATED,
        handler: (payload: NotificationPayload) => {
          try {
            if (payload.type !== ActionsOnReceive.NOTIFICATION_CREATED) return;
            store.dispatch(new NotificationActions.Add(payload.data.notification));
          } catch (err) {
            console.error(
              '[NotificationCreatedHandler] Failed to add notification:',
              err,
              payload,
            );
          }
        },
      };
    },
    multi: true,
  };
}

/**
 * Internal shape of the domain payload carried inside TribuNotification.payload
 * for postulations.update events.
 */
interface PostulationsUpdateInnerPayload {
  type: 'postulations.update';
  data: {
    userId: string;
    jobUid?: string;
    postulationUid?: string;
    status?: string;
    /** Grading trio, present when the push comes from an AI-grading conclusion. */
    aiGradingStatus?: RecruiterPostulationItemDto.AiGradingStatusEnum | null;
    aiGradingScore?: number | null;
    aiGradingBand?: string | null;
  };
}

/**
 * Registers the POSTULATIONS_UPDATE push-event handler.
 *
 * IMPORTANT — single-envelope / dual-action contract (DA7, risk ND-4):
 * The router fires this handler when POSTULATIONS_UPDATE appears in
 * `actionsOnReceive`, but the envelope payload is NOTIFICATION_CREATED-typed
 * (one envelope, two consumers). This handler must therefore:
 *   1. Narrow on payload.type === NOTIFICATION_CREATED.
 *   2. Dig into payload.data.notification.payload (the domain object) to
 *      extract postulationUid and status.
 *   3. Call PostulationsStateService.updateOne(postulationUid, { status }).
 *
 * Usage: add to the `providers` array of AppModule next to provideNotificationCreatedHandler().
 */
export function providePostulationsUpdateHandler(): Provider {
  return {
    provide: NOTIFICATION_HANDLER,
    useFactory: (): NotificationHandler => {
      const service = inject(PostulationsStateService);
      return {
        action: ActionsOnReceive.POSTULATIONS_UPDATE,
        handler: (payload: NotificationPayload) => {
          try {
            // The envelope payload type is NOTIFICATION_CREATED (single-envelope contract).
            if (payload.type !== ActionsOnReceive.NOTIFICATION_CREATED) return;

            const notification = payload.data.notification as TribuNotification;
            const inner = notification.payload as PostulationsUpdateInnerPayload | null;

            if (inner === null || inner === undefined) {
              console.warn(
                '[PostulationsUpdateHandler] notification.payload is null — skipping',
                notification.uid,
              );
              return;
            }

            if (inner.type !== ActionsOnReceive.POSTULATIONS_UPDATE) {
              console.warn(
                '[PostulationsUpdateHandler] Inner payload type mismatch',
                inner.type,
              );
              return;
            }

            const { postulationUid, status, aiGradingStatus, aiGradingScore, aiGradingBand } =
              inner.data;
            if (!postulationUid || !status) {
              console.warn(
                '[PostulationsUpdateHandler] Missing postulationUid or status — skipping',
                inner.data,
              );
              return;
            }

            // Only the keys the push carries: a status-only push must not blank the grading fields.
            const patch: Partial<RecruiterPostulationItemDto> = {
              status: status as RecruiterPostulationItemDto.StatusEnum,
            };
            if (aiGradingStatus !== undefined) patch.aiGradingStatus = aiGradingStatus;
            if (aiGradingScore !== undefined) patch.aiGradingScore = aiGradingScore;
            if (aiGradingBand !== undefined) patch.aiGradingBand = aiGradingBand;

            service.updateOne(postulationUid, patch);
          } catch (err) {
            console.error(
              '[PostulationsUpdateHandler] Failed to update postulation:',
              err,
              payload,
            );
          }
        },
      };
    },
    multi: true,
  };
}

/**
 * Maps terminal video-interview events to the recruiter chip status enum (ADR-2, DEV-177).
 * Defined here (front side) because RecordedInterviewStatusEnum is a presentation vocabulary
 * of the recruiter app — the contract speaks domain events, not chip states.
 */
const TERMINAL_EVENT_STATUS_MAP = {
  [ActionsOnReceive.VIDEO_INTERVIEW_REQUEST_COMPLETED]:
    RecruiterJobListItemDto.RecordedInterviewStatusEnum.READY,
  [ActionsOnReceive.VIDEO_INTERVIEW_REQUEST_FAILED]:
    RecruiterJobListItemDto.RecordedInterviewStatusEnum.FAILED,
} as const;

/**
 * Registers two NOTIFICATION_HANDLER entries (one per terminal event) that share
 * the same handler body. When a VIDEO_INTERVIEW_REQUEST_COMPLETED or _FAILED push
 * event arrives with a known `jobUid`, the handler patches the in-memory vacancy signal
 * with the resolved chip status — no page reload required (DEV-177).
 *
 * Two entries are required because NotificationHandler.action is a single ActionsOnReceive,
 * so each terminal event needs its own registered entry (design §5.2 / ADR-5).
 *
 * Usage: spread into the `providers` array of AppModule next to provideVideoInterviewProgressHandler():
 *   ...provideVideoInterviewTerminalHandler()
 */
export function provideVideoInterviewTerminalHandler(): Provider[] {
  function makeHandler(action: keyof typeof TERMINAL_EVENT_STATUS_MAP): Provider {
    return {
      provide: NOTIFICATION_HANDLER,
      useFactory: (): NotificationHandler => {
        const service = inject(VacantesStateService);
        return {
          action,
          handler: (payload: NotificationPayload) => {
            try {
              // Narrow on direct-typed terminal payload (ADR-4): no NOTIFICATION_CREATED envelope.
              if (
                payload.type !== ActionsOnReceive.VIDEO_INTERVIEW_REQUEST_COMPLETED &&
                payload.type !== ActionsOnReceive.VIDEO_INTERVIEW_REQUEST_FAILED
              ) {
                return;
              }
              const data = payload.data as VideoInterviewRequestCompletedData | VideoInterviewRequestFailedData;
              // SetNull / pre-producer guard (ADR-7): no jobUid → no-op
              if (!data.jobUid) return;
              const status = TERMINAL_EVENT_STATUS_MAP[payload.type];
              service.patchRecordedInterviewStatus(data.jobUid, status);
            } catch (err) {
              console.error(
                '[VideoInterviewTerminalHandler] Failed to patch vacancy status:',
                err,
                payload,
              );
            }
          },
        };
      },
      multi: true,
    };
  }

  return [
    makeHandler(ActionsOnReceive.VIDEO_INTERVIEW_REQUEST_COMPLETED),
    makeHandler(ActionsOnReceive.VIDEO_INTERVIEW_REQUEST_FAILED),
  ];
}

/**
 * Registers the VIDEO_INTERVIEW_PROGRESS push-event handler (Lane 3, DEV-172).
 *
 * Fires once per question video the worker finalizes. It MUST exist alongside the
 * ActionsOnReceive.VIDEO_INTERVIEW_PROGRESS constant: this is a HIGH-FREQUENCY event,
 * so the no-handler fallback (notifications.state.ts) would fire a full FetchAll PER
 * finalize — exactly what Lane 3 exists to avoid (spec §6.3, §13 item 17).
 *
 * It records the distinct questionId into VideoInterviewProgressStore (keyed by
 * requestId = VIR uid) for the monotonic real-time `i+1/N`. PATCH: it ALSO triggers a
 * silent vacancy-list re-fetch (VacantesStateService.refreshSilently) so the
 * "Creando entrevista…" base state flips to "Renderizando i/N" without a manual reload —
 * the base state does not self-update during generation and shadows the counter. No bell,
 * no NGXS FetchAll. Event-driven (one re-fetch per progress push, only while generating),
 * NOT polling.
 *
 * Usage: add to AppModule providers next to providePostulationsUpdateHandler().
 */
export function provideVideoInterviewProgressHandler(): Provider {
  return {
    provide: NOTIFICATION_HANDLER,
    useFactory: (): NotificationHandler => {
      const progress = inject(VideoInterviewProgressStore);
      const vacantes = inject(VacantesStateService);
      return {
        action: ActionsOnReceive.VIDEO_INTERVIEW_PROGRESS,
        handler: (payload: NotificationPayload) => {
          try {
            if (payload.type !== ActionsOnReceive.VIDEO_INTERVIEW_PROGRESS) return;
            const { requestId, questionId } = payload.data;
            progress.record(requestId, questionId);
            // PATCH: silent re-fetch so the chip flips from "Creando entrevista…" to
            // "Renderizando i/N" live. The base state (recordedInterviewStatus) does not
            // self-update during generation and shadows the counter. Event-driven (one
            // re-fetch per progress push, only while generating), NOT polling.
            vacantes.refreshSilently();
          } catch (err) {
            console.error(
              '[VideoInterviewProgressHandler] Failed to record progress:',
              err,
              payload,
            );
          }
        },
      };
    },
    multi: true,
  };
}

/** The two CV screening pushes; both only trigger refetches of the vacancy the panel shows. */
type CvScreeningAction =
  | ActionsOnReceive.CV_SCREENING_DOCUMENT_UPDATED
  | ActionsOnReceive.CV_SCREENING_JOB_READY;

/**
 * Registers one NOTIFICATION_HANDLER per CV screening push (SDD 2026-10-01-cv-screening-candidate-review,
 * D-CR-6). Claiming them keeps the no-handler FetchAll fallback away: `document-updated` arrives once
 * per CV. Both hand the push to CvScreeningLiveRefreshService, which refetches the vacancy the
 * «Preliminares» panel shows and ignores any other; the payload never feeds the state.
 *
 * Usage: spread into the `providers` array of AppModule next to provideVideoInterviewTerminalHandler():
 *   ...provideCvScreeningHandlers()
 */
export function provideCvScreeningHandlers(): Provider[] {
  function makeHandler(action: CvScreeningAction): Provider {
    return {
      provide: NOTIFICATION_HANDLER,
      useFactory: (): NotificationHandler => {
        const live = inject(CvScreeningLiveRefreshService);
        return {
          action,
          handler: (payload: NotificationPayload) => {
            try {
              // Narrow on the direct-typed payload: a mismatched one is not this event.
              if (payload.type !== action) return;
              if (payload.type === ActionsOnReceive.CV_SCREENING_DOCUMENT_UPDATED) {
                live.handleDocumentUpdated(payload.data);
              } else if (payload.type === ActionsOnReceive.CV_SCREENING_JOB_READY) {
                live.handleJobReady(payload.data);
              }
            } catch (err) {
              console.error('[CvScreeningHandler] Failed to refresh the vacancy:', err, payload);
            }
          },
        };
      },
      multi: true,
    };
  }

  return [
    makeHandler(ActionsOnReceive.CV_SCREENING_DOCUMENT_UPDATED),
    makeHandler(ActionsOnReceive.CV_SCREENING_JOB_READY),
  ];
}
